import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { ProductItemResponse } from '@/api/product/types';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import { useEmblaArrowButton, useIsMobile } from '@/helpers';
import { useProductPrices } from '@/helpers/useProductPrices';
import { breakpoints } from '@/types';
import { ArrowButton } from '../ArrowButton';
import { ProductCard, ProductCardProps } from '../ProductCard';
import styles from './ProductSlider.module.scss';

interface ProductSliderProps {
	className?: string;
	slidesWidth?: string | 'auto';
	title?: string;
	subtitle?: string;
	products?: ProductItemResponse[] | null;
	showArrows?: boolean;
	showArrowsOnMobile?: boolean;
	isLoading?: boolean;
	lazyLoading?: boolean;
	numberOfSkeletons?: number;
}

/**
 * ProductSlider component, used for showing product cards next to each other.
 */
export const ProductSlider: React.FunctionComponent<ProductSliderProps> = ({
	slidesWidth = '20%',
	showArrows = true,
	showArrowsOnMobile = false,
	isLoading = false,
	numberOfSkeletons = 5,
	className,
	title,
	subtitle,
	products,
	lazyLoading,
}) => {
	const sliderOptions: EmblaOptionsType = {
		align: 'start',
		loop: true,
		dragThreshold: 15,
	};

	const [emblaRef, emblaApi] = useEmblaCarousel(sliderOptions);
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const isMobile = useIsMobile();
	const layoutSwitch = 'sm';

	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = useEmblaArrowButton(emblaApi);

	const shouldRenderTitle = Boolean(title || subtitle);

	const productCards: ProductCardProps[] = useMemo(() => {
		return isLoading
			? Array.from(new Array(numberOfSkeletons)).map(() => ({ isSkeleton: true }))
			: (products ?? []).map((product) => ({ product }));
	}, [products, isLoading, numberOfSkeletons]);

	const { productsWithPrices, isPricesLoading, isPricesError } = useProductPrices(productCards, isLoading);

	return (
		<section className={classNames(styles.wrapper, className)}>
			{shouldRenderTitle || showArrows ? (
				<header
					className={classNames(styles.topWrapper, {
						[styles.hasMargin]: shouldRenderTitle || showArrows,
						[styles.noMarginOnMobile]: !shouldRenderTitle && !showArrowsOnMobile,
					})}
				>
					{shouldRenderTitle ? (
						<div className={styles.titleWrapper}>
							{subtitle && <p className={styles.subtitle}>{subtitle}</p>}
							{title && <h3 className={styles.title}>{title}</h3>}
						</div>
					) : null}
					{showArrows && (!isMobile || showArrowsOnMobile) ? (
						<nav
							className={classNames(styles.arrows, {
								[`u-hide-${layoutSwitch}-max`]: !showArrowsOnMobile, // hide with CSS until JS media query is calculated
							})}
							aria-label={translations?.data.shared.navigation.slider}
						>
							<ArrowButton
								className={styles.arrow}
								onClick={onPrevButtonClick}
								disabled={prevBtnDisabled}
								prev
							/>
							<ArrowButton
								className={styles.arrow}
								onClick={onNextButtonClick}
								disabled={nextBtnDisabled}
								next
							/>
						</nav>
					) : null}
				</header>
			) : null}

			<div className={styles.viewport} ref={emblaRef}>
				<ul className={styles.sliderWrapper}>
					{productsWithPrices.map((productCardProps, index) => (
						<li
							className={styles.slide}
							style={{ '--slide-width': slidesWidth } as React.CSSProperties}
							key={productCardProps.product?.id ?? index}
						>
							<ProductCard
								{...productCardProps}
								isPricesLoading={isPricesLoading}
								isPricesError={isPricesError}
								responsiveSizesSettings={`
								(min-width: 1480px) 320px,
								(min-width: ${breakpoints.sm}px) 24vw,
								160px
							`}
								lazyLoading={lazyLoading}
							/>
						</li>
					))}
				</ul>
			</div>
		</section>
	);
};
